import { analytics } from "components/common/analytics";
import type { TrackLocation } from "analytics/eventCatalog";
import { recordClickout } from "./returnAfterClickout";
import { trackOpenAI } from "utils/openaiPixel";

/**
 * Reproduces `moment(end).diff(start, "days")` for the three call sites below, without pulling in
 * moment (jsnext:main-only — see vite.config.ssr.ts's mainFields comment on why that used to force
 * disabling browser-field resolution for the whole marketing bundle).
 *
 * moment semantics this matches:
 *  - `end` null/undefined → "now" (none of the three call sites below ever pass an invalid
 *    non-date value for `end`, only a real date or nothing).
 *  - either side fails to parse → NaN (moment.diff returns NaN for an invalid moment).
 *  - DST-aware: moment's own diff subtracts each side's UTC-offset (in minutes, converted to ms)
 *    from its epoch ms before dividing by a fixed day length, so a span crossing a DST transition
 *    in the browser's local timezone still lands on a whole number of days instead of 23.96/24.04.
 *  - truncated toward zero (moment.diff floors a positive result and ceils a negative one — it
 *    never rounds).
 */
export function diffInDays(end: Date | string | number | null | undefined, start: Date | string | number | null | undefined): number {
	// Same inputs moment took: `undefined` means now, `null` is an invalid date (moment(null) is invalid).
	const toDate = (value: Date | string | number | null | undefined): Date => (value === undefined ? new Date() : value === null ? new Date(NaN) : new Date(value));
	const endDate = toDate(end);
	const startDate = toDate(start);

	if (isNaN(endDate.getTime()) || isNaN(startDate.getTime())) return NaN;

	const MS_PER_DAY = 24 * 60 * 60 * 1000;
	const endAdjusted = endDate.getTime() - endDate.getTimezoneOffset() * 60 * 1000;
	const startAdjusted = startDate.getTime() - startDate.getTimezoneOffset() * 60 * 1000;

	const diff = (endAdjusted - startAdjusted) / MS_PER_DAY;
	return diff < 0 ? Math.ceil(diff) : Math.floor(diff);
}

/**
 * Canonical surface where an event was triggered (the "source"/"location" dimension).
 *
 * This is a CLOSED enum — only canonical surface values are accepted. There is no
 * `(string & {})` escape hatch: ad-hoc strings will not compile. Add ad-hoc/route-derived
 * strings to the canonical set via `toTrackLocation()` in `affiliateTrackingUtils.ts`
 * rather than passing them through. A `tsc --noEmit` step in CI enforces this on every PR.
 *
 * Mobile (`stippl-app`) mirrors this vocabulary — see ClickUp 86ca9yxk0.
 *
 * Sourced from the canonical shared catalog (`analytics/eventCatalog.ts`) — re-exported here so
 * existing consumers that import `TrackLocation` from this module keep working.
 */
export type { TrackLocation };

// An affiliate_clicked is the start of a round trip: the user leaves for a partner and may come
// back. Stamping it here rather than at each call site means a new affiliate surface is covered the
// day it ships, with nothing to remember.
const stampClickout = (event: string) => {
	if (event === "affiliate_clicked") recordClickout();
};

// An affiliate deeplink is not a dimension: it is unique per click and routinely carries a
// click id. The answerable question is which partner the click went to, so only the host is
// forwarded. Never throws — some senders pass a relative or already-malformed URL.
const clickoutDomain = (url?: string): string | undefined => {
	if (!url || typeof url !== "string") return undefined;
	try {
		return new URL(url, "https://stippl.io").hostname;
	} catch {
		return undefined;
	}
};

const trackEvent = (
	event: //Inbound triggers
		| "ai_trip_locked_shown"
		| "returned_after_clickout"
		| "place_detail_viewed"
		| "bookings_tab_opened"
		| "click_login"
		| "create_handle"
		| "create_handle_failed"
		| "click_signup"
		| "user_signup"
		| "error_signup"
		| "signup_error"
		| "signup_attempt"
		| "in_app_browser_escape_failed"
		| "click_appstore_cta"
		| "click_googleplay_cta"
		| "click_signup_cta"
		| "open_storyview"
		| "organisation_invite_accepted"
		| "anonymous_converted"
		| "anonymous_cta_clicked"
		| "anonymous_bookmark_cta_clicked"
		| "anonymous_login"

		//Places Triggers
		| "click_add_article"
		| "click_add_sleep"
		| "click_add_see_do"
		| "click_add_eat_drink"
		| "add_article_to_plan"
		| "add_to_profile_article"
		| "add_to_profile_sleep"
		| "add_to_profile_see_do"
		| "add_to_profile_eat_drink"
		| "delete_from_profile_article"
		| "delete_from_profile_sleep"
		| "delete_from_profile_see_do"
		| "delete_from_profile_eat_drink"
		| "edit_note_place"
		| "update_recommendation_status"
		| "research_saved"
		| "search_profile"
		| "search_discover"
		| "search_creator"

		//Trip Behavior Triggers
		| "create_trip"
		| "copy_trip"
		| "copy_trip_files_email"
		| "delete_trip"
		| "open_trip"
		| "update_trip_privacy"
		| "add_to_trip_article"
		| "add_to_trip_sleep"
		| "add_to_trip_see_do"
		| "add_to_trip_eat_drink"
		| "add_to_trip_tour"
		// The live one. The five names above are wired to mutations that stopped being written in
		// Nov 2025 and have never once reached OpenPanel — see analytics/addToTrip.
		| "add_to_trip"
		| "add_to_trip_transport_route"
		| "add_to_trip_transport_ticket"
		| "delete_from_trip_article"
		| "delete_from_trip_sleep"
		| "delete_from_trip_see_do"
		| "delete_from_trip_eat_drink"
		| "delete_from_trip_tour"
		| "delete_from_trip_transport_route"
		| "delete_from_trip_transport_ticket"
		| "delete_from_trip_transport"
		| "add_to_trip_transport"
		| "update_transport"
		| "create_destination"
		// Mirrors the app's contract (123yb695ehd): fired instead of create_destination when an
		// add doesn't go through, so a rejected mutation stops being counted as a success.
		| "create_destination_failed"
		| "delete_destination"
		| "lock_destination"
		| "set_destination_date"
		| "open_trip_destination"
		| "edit_note_destination"
		| "notes_added"
		| "set_booked"
		| "search_destination_sleep"
		| "search_destination_discover"
		| "search_destination_transport_route"
		| "search_destination_transport_ticket"
		| "search_transport_tickets"
		| "search_transport_route"

		//Expense & Budget Triggers
		| "create_expense"
		| "open_expense"
		| "update_expense"
		| "delete_expense"
		// Mirrors the app's export_expenses (ExpenseExportButton.tsx:24): fired once the CSV export
		// actually goes through, not on click. Always empty props — location is the only dimension.
		| "export_expenses"
		| "settle_expenses"
		| "apply_budget_filters"
		| "create_custom_expense_category"

		//Checklist Triggers
		| "add_checklist_category"
		| "add_custom_checklist"
		| "add_checklist_item"
		| "item_packed"
		| "copy_packing_list"
		// Mirrors the app's packing_list_copied (123yb695h39): fired once the copy-from-a-previous-trip mutation
		// resolves, not on the tap that asked for it. Always empty props; location is the only dimension.
		| "packing_list_copied"

		//Journal Triggers
		| "create_journal"
		| "edit_journal"
		| "delete_journal"
		| "journal_liked"

		//PDF & Files Triggers
		| "pdf_export"
		| "download_pdf_export"
		| "open_trip_files"
		| "open_place_files"
		| "add_file_to_place"
		| "remove_file_from_place"
		// Mirrors the app's forward_files_used (123yb695h38): fired once a forwarded file is actually
		// assigned into the trip (DestinationModal.tsx), not when the "Forward" instructions modal
		// opens. Always empty props.
		| "forward_files_used"

		//AI Trip Generator
		| "open_trip_generator"
		| "generate_ai_trip"
		| "ai_trip_generated"
		| "ai_trip_generated_error"
		| "ai_generator: press_start"
		| "ai_generator: select_type"
		| "ai_generator: select_pace"
		| "ai_generator: select_round_trip"
		| "ai_generator: add_hint"
		| "ai_generator: press_cancel"
		| "ai_generator: go_back"
		| "ai_generator: exit_generation"

		//AI Destination Notes
		| "open_destination_notes_generator"
		| "destination_notes_generated"
		| "add_generated_notes"
		| "close_destination_notes_generator"

		// AI content generation (123yb695h38) — one event, `surface` distinguishes DayEditor.tsx
		// ("day_plan"), DescriptionEditor.tsx ("description") and the app's NotesGeneratorInput.tsx
		// ("notes"). Fired once the AI call resolves, never on the subscription-gate path.
		| "ai_content_generated"

		//Calendar
		| "open_calendar_sync"
		| "calendar_sync"

		// Map style — mirrors the app's save_map_style (123yb695ehe): fired when a user actually
		// applies a style on /mymap, not when the panel merely opens.
		| "save_map_style"

		// Video reels (123yb695h38) — mirrors the app's video_reel_created: fired once the reel
		// export/render request is actually submitted (VideoCreateModal.tsx), not on modal open.
		// Free users can create reels too, so `pro` records the tier, not a gate outcome.
		| "video_reel_created"

		//Share triggers
		| "click_invite_to_stippl"
		| "invite_to_stippl"
		| "click_trip_share"
		| "trip_share"
		| "click_invite_to_trip"
		| "invite_to_trip"
		| "user_invited_to_trip"
		| "click_profile_share"
		| "profile_share"
		| "click_discover_share"
		| "discover_share"
		| "share_invite_to_trip_link"
		| "invite_by_email"
		| "join_trip"
		| "invite_page_viewed"
		// "Make this route yours" CTA on /routepreview/:tripId (123yb69474b) -- the default switch
		// case below already shapes { ...data, location }, so no dedicated case is needed.
		| "route_preview_cta_clicked"
		// "Save this guide" CTA on /listpreview/:listId -- shaped by the same default case.
		| "list_preview_cta_clicked"
		// A creator's own link on a list item (Book / Tickets / Visit) on /listpreview/:listId: `{ list_id, place_id, host }`, `host` being the
		// link's hostname (never the URL: an affiliate link is unique per creator and may carry a click id). The default case forwards it.
		| "listpreview_item_link"
		// A partner page (/mad-monkey) created a trip for the visitor: `{ partner, stops, nights, source }`,
		// `source` being "builder" or the ready-made route's id. Fired once the trip exists, not on click.
		| "partner_trip_created"
		// A button or outbound link of a partner map page (/p/<slug>): `{ partner, action, item? }`, `action` being "save_map", "start_planning",
		// "whatsapp_signup", "contact", "tickets" (a third party's booking link) or "place_map" (a place's Google Maps link), and `item`
		// the place, event or contact channel it was on.
		| "partner_map_action"
		// The country hub's route map (/discover/<country>, discover/countryMap/): all go through the default case below, which forwards
		// `data` as it is. `countrymap_cta` carries `content` ("route" | "myroute") and `route_id`; the others a `route_id` or a `slug`.
		| "countrymap_tab"
		| "countrymap_route"
		| "countrymap_customise"
		| "countrymap_place"
		| "countrymap_add"
		| "countrymap_addsheet"
		| "countrymap_addregion"
		| "countrymap_reorder"
		| "countrymap_date"
		| "countrymap_browse_routes"
		| "countrymap_cta"
		| "countrymap_flex"
		| "countrymap_scratch"
		| "click_play_button"
		| "render_movie"
		| "re-render_movie"
		| "render_completed"

		//clickout triggers
		| "clickout_booking_button"
		| "clickout_airbnb_button"
		| "clickout_hostelworld_button"
		| "clickout_getyourguide_button"
		| "clickout_viator_button"
		| "clickout_tripadvisor_button"
		| "clickout_rentalcars_button"
		| "clickout_article"
		| "clickout_sleep"
		| "clickout_see_do"
		| "clickout_eat_drink"
		| "clickout_tour"
		| "clickout_skyscanner_button"
		| "clickout_omio_button"
		| "clickout_12asia_button"
		| "clickout_google_transport_button"
		| "clickout_transport_ticket"

		//profiles
		| "request_follow_user"
		| "follow_user"
		| "unfollow_user"
		| "accept_follow_request"
		| "decline_follow_request"
		| "open_notifications"
		| "update_profile"
		| "set_profile_picture"
		| "update_profile_privacy"
		| "search_user"
		| "profile_view"
		| "click_instagram_link"
		| "click_website_link"

		//Account deletion funnel — funnel 169 (ClickUp 123yb692gth)
		| "settings_opened"
		| "delete_account_section_viewed"
		| "delete_account_initiated"
		| "deletion_confirmed"
		| "deletion_cancelled"
		| "deletion_reason_selected"

		//B2B: Organisation
		| "b2b:create_organisation"
		| "b2b:update_organisation"
		| "b2b:delete_organisation"
		| "b2b:update_organisation_theme"

		//B2B: Team
		| "b2b:invite_employee"
		| "b2b:accept_invite"
		| "b2b:cancel_invite"
		| "b2b:remove_employee"
		| "b2b:update_employee"

		//B2B: Templates
		| "b2b:create_template"
		| "b2b:import_template_url"
		| "b2b:import_template_pdf"
		| "b2b:cancel_template_import"
		| "b2b:copy_template"
		| "b2b:delete_template"
		| "b2b:search_templates"

		//B2B: Clients
		| "b2b:create_client"
		| "b2b:update_client"
		| "b2b:delete_client"
		| "b2b:assign_trip_to_client"

		//B2B: Maps
		| "b2b:create_map"
		| "b2b:update_map"
		| "b2b:delete_map"

		//B2B: Place Lists
		| "b2b:create_list"
		| "b2b:add_place_to_list"
		| "b2b:remove_place_from_list"
		| "b2b:delete_list"

		//B2B: Image Library
		| "b2b:upload_image"
		| "b2b:create_folder"
		| "b2b:delete_folder"
		| "b2b:link_image"
		| "b2b:delete_image"

		//B2B: Subscription & Billing
		| "b2b:start_checkout"
		| "b2b:upgrade_subscription"
		| "b2b:add_addon"
		| "b2b:remove_addon"
		| "b2b:enable_paygo"
		| "b2b:open_billing_portal"
		| "b2b:hit_subscription_limit"
		| "b2b:update_pricing"

		//B2B: Analytics — Onboarding & Activation
		| "b2b:signup_completed"
		| "b2b:first_trip_created"
		| "b2b:first_proposal_sent"
		| "b2b:branding_configured"

		//B2B: Analytics — Trip & Itinerary
		| "b2b:trip_created"
		| "b2b:trip_published"
		| "b2b:trip_duplicated"
		| "b2b:trip_shared"
		| "b2b:pdf_export"
		| "b2b:day_added"
		| "b2b:activity_added"

		//B2B: Analytics — Content Library
		| "b2b:library_item_created"
		| "b2b:library_item_added_to_trip"
		| "b2b:library_item_edited"

		//B2B: Analytics — Maps
		| "b2b:map_code_copy"

		//B2B: Analytics — Travel Reels
		| "b2b:reel_created"

		//B2B: Analytics — Engagement
		| "b2b:proposal_opened"
		| "b2b:booking_link_clicked"

		//B2B: Analytics — Team
		| "b2b:team_member_invited"
		| "b2b:team_member_accepted"

		//Analytics — funnel additions
		| "open_create_trip"
		| "exit_offer_shown"
		| "landing_page_viewed"
		| "ai_chat_beta_signup"
		| "b2b_waitlist_signup"
		// Web-only: the /creators application form (graphql submitCreatorApplication) went through. Only `size` rides along, no personal data.
		| "creator_application_submitted"
		| "affiliate_clicked"
		| "affiliate_card_impression"
		| "affiliate_card_engaged"
		// Bookings tab engagement funnel entry half — funnel 184 (ClickUp 123yb692gdd)
		| "partner_carousel_impression"

		//Hotel (Nuitee) booking funnel — B-2 (86cawabu8)
		| "hotel_search_performed"
		| "hotel_card_impression"
		| "hotel_detail_viewed"
		// Web-only for now (123yb697nkv) — once per completed rooms fetch on a hotel detail page, an
		// empty list included (room_count 0). See hotelBookingAnalytics.ts / eventCatalog.ts.
		| "hotel_rooms_loaded"
		// Shared with the app's booking redesign (123yb697nh9) — a room's details were opened.
		// See hotelBookingAnalytics.ts / eventCatalog.ts.
		| "room_card_opened"
		// Web-only (123yb697nh9) — a rate's price breakdown was opened; the app has no rate breakdown.
		| "rate_details_opened"
		// Shared with the app (CU 123yb698zhq) — the hotel page's full facilities list was opened (web: the Amenities tab).
		| "hotel_facilities_opened"
		| "room_selected"
		| "checkout_started"
		| "checkout_submitted"
		| "booking_completed"
		| "checkout_abandoned"
		| "checkout_3ds_triggered"
		// Mobile-first (123yb695mzp) — every checkout error the client can observe: a prebook
		// error/RATE_CHANGED, a Stripe confirmPayment error (including user-cancel), a failed 3DS
		// redirect, or a book-mutation error/no-longer-available/charged-but-no-booking-id. Mobile has
		// sent it since 3.1.5; web sends it from this change (123yb695n0e) — see
		// hotelBookingAnalytics.ts. Codes only, never the raw error message or card/guest detail.
		| "checkout_failed"
		// Mobile-first — the streaming search's own completion (result_count, duration_ms), which is
		// what makes a zero-result search visible at all. Mobile has sent it since 3.1.5; web sends
		// it from this change.
		| "hotel_search_completed"
		// Mobile-first — fires once per user-initiated sort/filter change (not per render/dispatch
		// made on the guest's behalf). Mobile has sent it since 3.1.5; web sends it from this change.
		| "hotel_search_refined"
		| "compare_bar_expanded"
		| "stay_search_channel_selected"
		| "hotel_price_engaged"
		| "cancel_booking_initiated"
		| "cancel_booking_dismissed"
		| "cancel_booking_confirmed"
		| "booking_cancelled"
		| "booking_date_change_initiated"
		| "booking_date_change_submitted"
		| "booking_date_changed"

		//B2C: Paywall
		| "b2c:paywall_present_attempted"
		| "b2c:paywall_shown"
		| "b2c:paywall_dismissed"
		| "b2c:paywall_subscribed"
		| "b2c:paywall_error"

		//B2C: Explore
		| "open_explore_country"
		| "open_explore_destination"
		| "click_explore_cta"

		//Trip Readiness
		| "open_trip_readiness"
		| "readiness_add_stay"
		| "readiness_next_action"

		//Stays: manual add (web)
		| "add_booked_stay_clicked"

		//Reel Preview
		| "reel_preview_share_click"
		| "reel_preview_open_app_click"
		| "reel_preview_page_view"
		| "reel_preview_page_not_found"

		//Blog articles (knowledge-base pipeline — see work/blog-content-pipeline.md)
		//
		// `blog_block_clicked` carries the block type, which is the whole point: 19 content
		// blocks ship without anyone being able to say which of them a reader ever acts on,
		// and that is the question that decides whether the library is worth extending.
		| "blog_article_viewed"
		| "blog_block_clicked"
		| "blog_related_clicked"

		//Content Sharing (generic — surfaces reused across content types, e.g. GeneralShareModal)
		| "content_shared"

		//AI Chat — clarification chips (86c9c2ufd) and the intro's suggested prompts (work/ai-chat-phase-openers.md)
		| "ai_chat_clarification_option_selected"
		| "ai_chat_suggested_prompt_tapped"
		| "ai_chat_suggestion_applied"

		//Banners — canonical shown/cta_clicked/dismissed trio, see analytics/banners.ts. Mirrors
		//stippl-app's own trio (src/analytics/banners.ts there) so a banner's funnel is joinable
		//across platforms; the `default` case below already shapes the payload correctly (banner_id
		//plus action/method are assembled by the caller), so no dedicated switch case is needed.
		| "banner_shown"
		| "banner_cta_clicked"
		| "banner_dismissed"

		//Link-in-bio page (/links, links.stippl.io) — see work/stippl-links-page.md. All three go through
		//the `default` case below, which forwards every property; none needs a dedicated switch case.
		| "links_page_viewed"
		| "links_click"
		| "links_folder_open"

		//ChatGPT/Claude plugin consent page (/connect) — see work/chatgpt-claude-plugin.md. All four go
		//through the `default` case below, which forwards every property; none needs a dedicated switch case.
		| "connect_page_viewed"
		| "connect_approved"
		| "connect_cancelled"
		| "connect_password_reset_requested"

		//ChatGPT/Claude landing pages (/chatgpt-travel-planner, /claude-travel-planner) — one event for every
		//CTA there, told apart by `placement`. Goes through the `default` case below, which forwards every property.
		| "plugin_page_cta_clicked",

	data: any,
	location: TrackLocation
) => {
	stampClickout(event);
	// Every signup surface (RegisterBox, B2B form, AcceptInvite) already reports user_signup here.
	if (event === "user_signup") trackOpenAI("registration_completed");

	switch (event) {
		// `location` is sent for the /connect username step only, so it can be told apart from onboarding's;
		// every other caller's payload stays { handle }.
		case "create_handle":
			analytics.track(event, location === "plugin_connect" ? { handle: data, location } : { handle: data });
			break;
		// A missing status (network failure/timeout) is its own meaningful category, so it is
		// forwarded as `null` rather than left as `undefined` — analytics.track's payload is
		// JSON-serialized before it is sent, and JSON.stringify drops undefined-valued keys.
		case "create_handle_failed":
			analytics.track(event, { status: data?.status ?? null, location });
			break;
		case "click_login":
		case "click_signup":
		case "user_signup":
			analytics.track(event, {
				identity_type: data?.identity_type ? data?.identity_type : data,
				error: data?.error,
				// Signup attribution. RegisterBox has been sending stippl_campaign, AcceptInvite
				// and the B2B form have been sending source, and this case discarded all of it —
				// while attribution stayed on the open-questions list. Safe against the senders
				// that pass a bare string as `data`; these read undefined there.
				source: data?.source,
				stippl_campaign: data?.stippl_campaign,
				company: data?.company,
				// Set when the signup began on a public preview page (analytics/signupOrigin); undefined elsewhere.
				signup_origin: data?.signup_origin,
				origin_id: data?.origin_id,
				location,
			});
			break;
		case "add_to_profile_article":
		case "add_to_profile_sleep":
		case "add_to_profile_see_do":
		case "add_to_profile_eat_drink":
			analytics.track(event, {
				location: location,
				content_owner_type: data.content_owner_type,
				content_owner_username: data.content_owner_username,
				category: data.category,
				city_name: data.city,
				country_name: data.country,
				source_type: data.source_type,
				url: data.url,
			});
			break;
		// One event, category as a prop. Fired from the wrappers in
		// components/common/useTrackedTripAdd, not from individual buttons.
		case "add_to_trip":
			analytics.track(event, {
				location,
				route: data?.route,
				category: data?.category,
				place_id: data?.place_id,
				trip_id: data?.trip_id,
				source: data?.source,
				in_trip: data?.in_trip,
				placement: data?.placement,
			});
			break;
		case "add_to_trip_article":
		case "add_to_trip_sleep":
		case "add_to_trip_see_do":
		case "add_to_trip_eat_drink":
		case "add_to_trip_tour":
		case "delete_from_trip_article":
		case "delete_from_trip_sleep":
		case "delete_from_trip_see_do":
		case "delete_from_trip_eat_drink":
		case "delete_from_trip_tour":
			analytics.track(event, {
				location: location,
				content_owner_type: data.content_owner_type,
				content_owner_username: data.content_owner_username,
				category: data.category,
				city_name: data.city,
				country_name: data.country,
				source_type: data.source_type,
				property_name: data.propertyName,
				url: data.url,
			});
			break;
		case "create_trip":
		case "delete_trip":
			analytics.track(event, {
				users_invited: data?.userProfiles?.length - 1,
				countries: data?.countries?.map((country) => country?.name),
				duration: diffInDays(data?.endDate, data?.startDate),
				number_of_countries: data?.countries?.length,
				start_date: data?.startDate,
				end_date: data?.endDate,
				name: data?.name,
				trip_id: data?._id,
				// Which wizard path produced the trip, and where it started from.
				method: data?.method,
				source: data?.source,
				// 1-indexed count of trips this user has created, computed server-side on Trip.tripOrdinal
				// (funnel 16, second-trip-creation). Only create_trip callers populate it; delete_trip
				// callers pass a trip without this field, so it — like the other undefined keys above —
				// is dropped by JSON.stringify before send.
				trip_ordinal: data?.tripOrdinal,
				location,
			});
			break;
		case "copy_trip":
		case "open_storyview":
			analytics.track(event, {
				countries: data?.countries?.map((country) => country?.name),
				duration: diffInDays(data?.endDate, data?.startDate),
				number_of_countries: data?.countries?.length,
				start_date: data?.startDate,
				end_date: data?.endDate,
				name: data?.name,
				trip_id: data?._id,
				content_owner_type: data?.author?.isCreator,
				content_owner_username: data?.author?.username,
				share_id: data?.share_id,
				is_logged_in: data?.is_logged_in,
				location,
			});
			break;
		case "create_destination":
		case "delete_destination":
		case "lock_destination":
		case "set_destination_date":
		case "open_trip_destination":
			analytics.track(event, {
				location,
				city_name: data.city,
				country_name: data.country,
			});
			break;
		case "create_destination_failed":
			analytics.track(event, {
				location,
				reason: data?.reason,
			});
			break;
		case "ai_content_generated":
			analytics.track(event, {
				location,
				surface: data?.surface,
			});
			break;
		case "search_destination_sleep":
		case "search_destination_discover":
			analytics.track(event, {
				city_name: data.city,
				country_name: data.country,
				duration: data.duration,
				start_date: data.startDate,
				end_date: data.endDate,
				// Senders were already passing these and this switch was dropping them on the
				// floor, so ExploreSearchOverlay's picks landed with no destination on them at
				// all. A case that names its properties silently discards everything else.
				position: data.position,
				query: data.query,
				// ExploreSearchOverlay identifies its pick by slug, not by display name. Kept as
				// its own property rather than folded into country_name, which carries display
				// names from DestinationRow — merging them would give one country two spellings.
				destination: data.destination,
				location,
			});
			break;
		case "search_discover":
			analytics.track(event, {
				location,
				query: data?.query,
				result_type: data?.result_type,
				result_name: data?.result_name,
				// ExploreSearchOverlay has been sending these since it shipped and none of them
				// arrived. `has_results` is the whole point of measuring a search box — it is how
				// we see the demand we turn away — so losing it made the event nearly decorative.
				results: data?.results,
				has_results: data?.has_results,
				filtered: data?.filtered,
			});
			break;
		case "open_explore_country":
			analytics.track(event, {
				location,
				country_name: data?.country_name,
				source: data?.source,
			});
			break;
		case "open_explore_destination":
			analytics.track(event, {
				location,
				country_name: data?.country_name,
				city_name: data?.city_name,
				source: data?.source,
			});
			break;
		case "click_explore_cta":
			analytics.track(event, {
				location,
				source: data?.source,
				placement: data?.placement,
			});
			break;
		case "search_destination_transport_route":
			analytics.track(event, {
				location,
				cities: data.cities,
				countries: data.countries,
			});
			break;
		case "search_destination_transport_ticket":
		case "add_to_trip_transport_route":
		case "add_to_trip_transport_ticket":
		case "delete_from_trip_transport_route":
		case "delete_from_trip_transport_ticket":
			analytics.track(event, {
				location,
				cities: data.cities,
				vehicle: data.vehicle,
				// Every Rome2Rio surface sends this and none of it arrived, so no transport
				// interaction could be attributed to where it happened.
				placement: data?.placement,
			});
			break;
		case "add_to_trip_transport":
		case "delete_from_trip_transport":
			analytics.track(event, {
				location,
				cities: data.cities,
				travelMode: data?.transport?.segments?.[0]?.travelMode,
				isTicket: data?.isTicket,
				...data?.transport,
			});
			break;
		case "update_transport":
			analytics.track(event, {
				location,
				travelMode: data?.travelMode,
				...data?.fields,
			});
			break;
		case "click_invite_to_stippl":
		case "click_trip_share":
		case "click_profile_share":
		case "click_discover_share":
			analytics.track(event, {
				location: location,
				// FrontPage passes the whole trip. Only the identifying fields are forwarded —
				// a trip document is not a property.
				trip_id: data?.trip?._id,
				trip_name: data?.trip?.name,
			});
			break;
		case "click_invite_to_trip":
			analytics.track(event, {
				location: location,
				type: data?.type,
			});
			break;
		case "invite_to_stippl":
			analytics.track(event, {
				share_type: data.share_type,
			});
			break;
		case "trip_share":
		case "invite_to_trip":
		case "share_invite_to_trip_link":
		case "invite_by_email":
		case "join_trip":
		case "click_play_button":
		case "render_movie":
		case "re-render_movie":
		case "render_completed":
		// Fired with `{ trip }` once an AI-generated trip exists (HomePage "Plan with AI", Discover's
		// trip builder), so it carries the same trip fields as the events above. The attempt event,
		// generate_ai_trip, deliberately has no case: it forwards its raw input through `default`.
		case "ai_trip_generated":
			analytics.track(event, {
				share_type: data.share_type,
				invite_type: data?.invite_type,
				hasTripMovie: data?.trip?.hasTripMovie,
				number_of_users: data?.trip?.userProfiles?.length,
				countries: data?.trip?.countries?.map((country) => country?.name),
				duration: data?.trip ? diffInDays(data?.trip?.endDate, data?.trip?.startDate) : undefined,
				number_of_countries: data?.trip?.countries?.length,
				start_date: data?.trip?.startDate,
				end_date: data?.trip?.endDate,
				name: data?.trip?.name,
				trip_id: data?.trip?._id,
				content_owner_type: data?.content_owner_type,
				content_owner_username: data?.trip?.author?.username,
				share_id: data?.share_id,
				is_logged_in: data?.is_logged_in,
				// render_completed distinguishes rendering your own trip from someone else's.
				own_trip: data?.ownTrip,
				location,
			});
			break;
		case "profile_share":
			analytics.track(event, {
				country: data?.country,
				city: data?.city,
				share_type: data?.share_type,
				content_owner_type: data?.content_owner_type,
				content_owner_username: data?.content_owner_username,
				location,
			});
			break;
		case "discover_share":
			analytics.track(event, {
				country: data?.country,
				city: data?.city,
				share_type: data?.share_type,
				location,
			});
			break;
		case "clickout_booking_button":
		case "clickout_airbnb_button":
		case "clickout_hostelworld_button":
		case "clickout_getyourguide_button":
		case "clickout_viator_button":
		case "clickout_tripadvisor_button":
			analytics.track(event, {
				country: data?.country,
				city: data?.normalised_city,
				duration: data?.duration,
				start_date: data?.startDate,
				end_date: data?.endDate,
				property_name: data?.propertyName,
				type: data?.type,
				category: data?.category,
				// The raw `url` the sender passes is deliberately not forwarded — see clickoutDomain.
				clickout_domain: clickoutDomain(data?.url),
				location,
			});
			break;
		case "clickout_rentalcars_button":
		case "clickout_skyscanner_button":
		case "clickout_12asia_button":
		case "clickout_omio_button":
		case "clickout_google_transport_button":
			analytics.track(event, {
				cities: data?.cities,
				placement: data?.placement,
				location,
			});
			break;
		case "clickout_sleep":
		case "clickout_see_do":
		case "clickout_tour":
		case "clickout_eat_drink":
		case "clickout_article":
			analytics.track(event, {
				content_owner_type: data?.content_owner_type,
				content_owner_username: data?.content_owner_username,
				category: data?.category,
				source_type: data?.source_type,
				url: data?.url, //He?
				city_name: data?.city_name,
				country_name: data?.country_name,
				affiliate: data?.affiliate,
				location,
			});
			break;
		case "clickout_transport_ticket":
			analytics.track(event, {
				vehicle_type: data?.item?.legs?.map((leg) => leg?.vehicle_type),
				cities: `${data?.destinationFrom?.name} - ${data?.destinationTo?.name}`,
				placement: data?.placement,
				location,
			});
			break;
		case "create_expense":
		case "open_expense":
		case "update_expense":
		case "delete_expense":
			analytics.track(event, {
				place_type: data?.category,
				currency: data?.currency,
				city: data?.destination,
			});
			break;
		// Always empty props (123yb695h38) — location is the only dimension this event carries.
		case "export_expenses":
			analytics.track(event, { location });
			break;
		// Always empty props (123yb695h38) — location is the only dimension this event carries.
		case "forward_files_used":
			analytics.track(event, { location });
			break;
		case "add_checklist_category":
		case "add_custom_checklist":
			analytics.track(event, {
				list_name: data?.list?.name,
				list_type: data?.list?.type,
				add_to_all: data?.add_to_all,
			});
			break;
		case "add_checklist_item":
		case "item_packed":
			analytics.track(event, {
				item_name: data?.item_name,
				list_name: data?.list?.name,
				list_type: data?.list?.type,
			});
			break;
		case "profile_view":
			analytics.track(event, {
				username: data?.username,
				isCreator: data?.isCreator,
				location: location,
			});
			console.log(event, {
				username: data?.username,
				isCreator: data?.isCreator,
				location: location,
			});
			break;
		case "follow_user":
		case "request_follow_user":
		case "unfollow_user":
		case "accept_follow_request":
		case "decline_follow_request":
			// `location` distinguishes where the action happened (e.g. the notification panel's
			// inline row vs. the profile's own pending-requests list).
			analytics.track(event, {
				user: data.user,
				targetUser: data.targetUser,
				// The share page (location share_link) follows from several places and right after the
				// sign-up modal: which button (`placement`), on which trip, and how the viewer got there
				// (`via`: member / signup / login). Senders that don't pass them send undefined, dropped.
				placement: data?.placement,
				trip_id: data?.trip_id,
				via: data?.via,
				previous_status: data?.previous_status,
				location,
			});
			break;
		case "b2b:trip_shared":
			analytics.track(event, { method: data?.method, ...data, location });
			break;
		case "save_map_style":
			analytics.track(event, {
				location,
				mapStyle: data?.mapStyle,
				pro: data?.pro,
			});
			break;
		// The reason step is optional and analytics-only — never sent anywhere else. A case that
		// names its props explicitly (rather than falling through to `default`'s spread) is what
		// keeps a contract like this from silently absorbing an unrelated payload shape later — see
		// create_destination_failed for the same reasoning.
		case "deletion_reason_selected":
			analytics.track(event, {
				reason: data?.reason,
				location,
			});
			break;
		case "video_reel_created":
			analytics.track(event, {
				location,
				pro: Boolean(data?.pro),
			});
			break;
		default:
			analytics.track(event, { ...data, location: location });

			break;
	}
};

export default trackEvent;
